import { $toggleLink } from '@lexical/link';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import type { LucideIcon } from 'lucide-react';
import { Bold, Italic, Link, List, ListOrdered, MoreVertical, Strikethrough, Underline } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { VoiceRecordingButton } from '../../voice-recording/components/voice-recording-button.tsx';

function Divider({ className }: { className?: string }): React.ReactNode {
    return <div className={cn('w-px min-w-px h-4 bg-neutral-300 mx-1 shrink-0', className)} />;
}

type ToolbarItem = {
    key: string;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
    selected?: boolean;
    alwaysVisible?: boolean;
    showDividerBefore?: boolean;
};

type Props = {
    disableVoiceRecording?: boolean;
    className?: string;
};

export function ToolbarPlugin({ disableVoiceRecording = false, className }: Props): ReactNode {
    const { enableVoiceRecording } = useAppConfig();
    const { t } = useLocale();
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    // These states are registered with editor commands; keep them for future use.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_canUndo, setCanUndo] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_canRedo, setCanRedo] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
        }
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor, $updateToolbar]);

    const insertTranscribedText = useCallback(
        (text: string) => {
            editor.update(() => {
                const root = $getRoot();

                if (!root.getTextContent()) {
                    root.clear();
                }

                const paragraphNode = $createParagraphNode();
                const textNode = $createTextNode(text);
                paragraphNode.append(textNode);
                root.append(paragraphNode);
            });
        },
        [editor],
    );

    const insertLink = useCallback(() => {
        const url = prompt(t('rich_text_editor__enter_url'));
        if (url) {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $toggleLink(url, {
                        target: '_blank',
                    });
                }
            });
        }
    }, [editor, t]);

    const showVoiceRecordingButton = enableVoiceRecording && !disableVoiceRecording;

    const toolbarItems: ToolbarItem[] = useMemo(
        () => [
            {
                key: 'bold',
                icon: Bold,
                label: t('rich_text_editor__format_bold'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),
                selected: isBold,
                alwaysVisible: true,
            },
            {
                key: 'italic',
                icon: Italic,
                label: t('rich_text_editor__format_italic'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),
                selected: isItalic,
                alwaysVisible: true,
            },
            {
                key: 'underline',
                icon: Underline,
                label: t('rich_text_editor__format_underline'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline'),
                selected: isUnderline,
                alwaysVisible: true,
            },
            {
                key: 'strikethrough',
                icon: Strikethrough,
                label: t('rich_text_editor__format_strikethrough'),
                onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough'),
                selected: isStrikethrough,
                alwaysVisible: true,
            },
            {
                key: 'numbered-list',
                icon: ListOrdered,
                label: t('rich_text_editor__insert_numbered_list'),
                onClick: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
                showDividerBefore: true,
                alwaysVisible: true,
            },
            {
                key: 'bullet-list',
                icon: List,
                label: t('rich_text_editor__insert_bullet_list'),
                onClick: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
            },
            {
                key: 'link',
                icon: Link,
                label: t('rich_text_editor__insert_link'),
                onClick: insertLink,
                showDividerBefore: true,
            },
        ],
        [t, editor, isBold, isItalic, isUnderline, isStrikethrough, insertLink],
    );

    return (
        <div className={cn('flex flex-col relative pt-2', className)}>
            <div className="flex flex-row justify-between items-center">
                <div className="flex items-center gap-1 flex-1" ref={toolbarRef}>
                    {/* Toolbar buttons */}
                    {toolbarItems.map((item) => (
                        <span key={item.key} className="contents">
                            {item.showDividerBefore && (
                                <Divider
                                    className={cn('sm:block', {
                                        hidden: !item.alwaysVisible,
                                    })}
                                />
                            )}
                            <Tooltip>
                                <TooltipTrigger className={item.alwaysVisible ? undefined : 'hidden sm:flex'}>
                                    <Button
                                        variant="ghost"
                                        size="icon-s"
                                        className="hover:bg-transparent shrink-0"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            item.onClick();
                                        }}
                                        selected={item.selected}
                                        aria-label={item.label}
                                    >
                                        <item.icon className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{item.label}</TooltipContent>
                            </Tooltip>
                        </span>
                    ))}

                    <Divider className="sm:hidden" />

                    {/* Overflow dropdown - visible only on mobile */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-s"
                                className="hover:bg-transparent shrink-0 sm:hidden"
                                aria-label={t('rich_text_editor__more_options')}
                            >
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            {toolbarItems.map((item) => (
                                <DropdownMenuItem
                                    key={item.key}
                                    onSelect={item.onClick}
                                    selected={item.selected}
                                >
                                    <item.icon className="h-4 w-4" />
                                    <Text size="sm">{item.label}</Text>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="flex items-center gap-2">
                    {showVoiceRecordingButton && (
                        <VoiceRecordingButton onTranscription={insertTranscribedText} />
                    )}
                </div>
            </div>
        </div>
    );
}
